// Single Amazon Transcribe streaming recognizer. Captures the mic, fetches a
// short-lived presigned wss:// URL from the backend, then streams PCM16 frames
// wrapped in AWS EventStream binary framing. No @aws-sdk/client-transcribe-
// streaming (bundle bloat) — just the eventstream codec for exact framing.
import {EventStreamCodec} from '@smithy/eventstream-codec';
import {fromUtf8, toUtf8} from '@smithy/util-utf8';
import type {
  GetPresignedUrl,
  SttError,
  StreamingHandlers,
  StreamingRecognizer,
} from 'src/types/speech-to-text';
import logger from 'src/utils/logger';


const codec = new EventStreamCodec(toUtf8, fromUtf8);

// Worklet buffers this many samples before posting; 4096 @ 16kHz ≈ 256ms.
const FRAME_SIZE = 4096;
// After end-of-stream, wait this long for AWS to flush the last final result.
const FLUSH_MS = 1500;

// AudioWorklet processor source. Runs off the main thread: accumulates mic
// Float32 samples into FRAME_SIZE chunks, converts to Int16 little-endian PCM,
// and posts the raw bytes back (transferred, not copied). Built as a Blob URL
// at runtime so no bundler worklet-asset config is needed.
const PCM_WORKLET_SOURCE = `
class PcmProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this._frame = ${FRAME_SIZE};
    this._buf = new Float32Array(this._frame);
    this._off = 0;
  }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (!ch) return true;
    for (let i = 0; i < ch.length; i++) {
      this._buf[this._off++] = ch[i];
      if (this._off === this._frame) {
        const view = new DataView(new ArrayBuffer(this._frame * 2));
        for (let j = 0; j < this._frame; j++) {
          const s = Math.max(-1, Math.min(1, this._buf[j]));
          view.setInt16(j * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
        }
        this.port.postMessage(view.buffer, [view.buffer]);
        this._off = 0;
      }
    }
    return true;
  }
}
registerProcessor('pcm-processor', PcmProcessor);
`;

function mapMediaError(err: unknown): SttError {
  const name = (err as {name?: string} | null)?.name;
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return {code: 'permission-denied', message: 'Microphone access denied'};
  }
  if (name === 'NotFoundError') {
    return {code: 'no-microphone', message: 'No microphone found'};
  }
  return {code: 'unknown', message: 'Could not access microphone'};
}

// Wrap PCM bytes (or empty body for end-of-stream) as an AWS AudioEvent frame.
function encodeAudioEvent(pcm: Uint8Array): Uint8Array {
  return codec.encode({
    headers: {
      ':message-type': {type: 'string', value: 'event'},
      ':event-type': {type: 'string', value: 'AudioEvent'},
      ':content-type': {type: 'string', value: 'application/octet-stream'},
    },
    body: pcm,
  });
}

type Decoded =
  | {kind: 'transcript'; results: Array<{text: string; partial: boolean}>}
  | {kind: 'error'; message: string}
  | {kind: 'ignore'};

// Decode an incoming frame into transcript results or an error.
function decodeFrame(data: ArrayBuffer): Decoded {
  const msg = codec.decode(new Uint8Array(data));
  const messageType = msg.headers[':message-type']?.value;
  const eventType = msg.headers[':event-type']?.value;
  if (messageType === 'exception') {
    const body = toUtf8(msg.body);
    return {kind: 'error', message: body || 'Transcribe exception'};
  }
  if (eventType !== 'TranscriptEvent') {
    return {kind: 'ignore'};
  }
  try {
    // AWS TranscriptEvent JSON shape is untyped here; map to our minimal result.
    const payload = JSON.parse(toUtf8(msg.body)) as any;
    const rawResults = payload?.Transcript?.Results ?? [];
    const results = rawResults
      .map((r: any) => ({
        text: r?.Alternatives?.[0]?.Transcript ?? '',
        partial: Boolean(r?.IsPartial),
      }))
      .filter((r: {text: string}) => r.text);
    return {kind: 'transcript', results};
  } catch (err) {
    logger.error('transcribe: failed to parse TranscriptEvent', err);
    return {kind: 'ignore'};
  }
}

export interface CreateStreamingRecognizerOptions {
  lang?: string;
  getPresignedUrl: GetPresignedUrl;
  /** Empty string = system default mic; otherwise the chosen device's id. */
  deviceId?: string;
}

function createAmazonTranscribeRecognizer(
  getPresignedUrl: GetPresignedUrl,
  languageCode: string,
  deviceId: string,
): StreamingRecognizer {
  let socket: WebSocket | null = null;
  let stream: MediaStream | null = null;
  let audioContext: AudioContext | null = null;
  let workletNode: AudioWorkletNode | null = null;
  let workletUrl: string | null = null;
  // `cancelled` aborts everything (discard). `stopped` halts mic sends but
  // keeps processing incoming frames so AWS's post-EOS final flush still lands.
  let cancelled = false;
  let stopped = false;

  const teardownAudio = () => {
    if (workletNode) {
      workletNode.port.onmessage = null;
      workletNode.disconnect();
      workletNode = null;
    }
    if (workletUrl) {
      URL.revokeObjectURL(workletUrl);
      workletUrl = null;
    }
    stream?.getTracks().forEach((track) => track.stop());
    stream = null;
    audioContext?.close().catch(() => undefined);
    audioContext = null;
  };

  const closeSocket = () => {
    try {
      socket?.close();
    } catch {
      // already closing/closed; ignore.
    }
    socket = null;
  };

  const start = async (handlers: StreamingHandlers): Promise<void> => {
    cancelled = false;
    stopped = false;

    // 1. Mic FIRST — the presigned URL expires 15s after it is minted, so the
    //    permission prompt must clear before we fetch it.
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          // Empty deviceId = let the browser pick the system default.
          ...(deviceId ? {deviceId: {exact: deviceId}} : {}),
        },
      });
    } catch (err) {
      logger.error('transcribe recognizer: mic access failed', err);
      throw mapMediaError(err);
    }

    // Request 16kHz; read the rate the browser actually gave us and sign the
    // URL for that exact rate so the PCM we send matches (no resampling).
    audioContext = new AudioContext({sampleRate: 16000});
    const sampleRate = audioContext.sampleRate;

    // Load the PCM worklet and wire mic -> worklet -> destination. The worklet
    // posts Int16 PCM frames; we wrap each in an AudioEvent and send once the
    // socket is open (frames before then are dropped — the loss is sub-second).
    try {
      workletUrl = URL.createObjectURL(
        new Blob([PCM_WORKLET_SOURCE], {type: 'application/javascript'}),
      );
      await audioContext.audioWorklet.addModule(workletUrl);
    } catch (err) {
      teardownAudio();
      logger.error('transcribe recognizer: worklet load failed', err);
      throw {
        code: 'unknown',
        message: 'Could not start audio capture',
      } as SttError;
    }
    const source = audioContext.createMediaStreamSource(stream);
    workletNode = new AudioWorkletNode(audioContext, 'pcm-processor');
    workletNode.port.onmessage = (e: MessageEvent<ArrayBuffer>) => {
      if (cancelled || stopped || socket?.readyState !== WebSocket.OPEN) {
        return;
      }
      socket.send(encodeAudioEvent(new Uint8Array(e.data)));
    };
    source.connect(workletNode);
    workletNode.connect(audioContext.destination);

    // 2. Fetch the presigned URL.
    let url: string;
    try {
      const res = await getPresignedUrl({
        languageCode,
        sampleRate,
        mediaEncoding: 'pcm',
      });
      url = res.url;
    } catch (err) {
      teardownAudio();
      logger.error('transcribe recognizer: presigned URL fetch failed', err);
      throw {
        code: 'token',
        message: 'Could not obtain a transcription URL',
      } as SttError;
    }

    // 3. Open the socket and resolve once it is live.
    await new Promise<void>((resolve, reject) => {
      const ws = new WebSocket(url);
      ws.binaryType = 'arraybuffer';
      socket = ws;
      let opened = false;

      ws.onopen = () => {
        // Audio graph is already running; the worklet's port handler starts
        // sending frames now that the socket is open.
        opened = true;
        resolve();
      };

      ws.onmessage = (event) => {
        if (cancelled) {
          return;
        }
        const decoded = decodeFrame(event.data as ArrayBuffer);
        if (decoded.kind === 'error') {
          logger.error('transcribe stream exception', decoded.message);
          handlers.onError({code: 'network', message: decoded.message});
          return;
        }
        if (decoded.kind === 'transcript') {
          decoded.results.forEach((r) =>
            r.partial ? handlers.onInterim(r.text) : handlers.onFinal(r.text),
          );
        }
      };

      ws.onerror = () => {
        if (!opened) {
          reject({
            code: 'network',
            message: 'Could not connect to transcription',
          } as SttError);
        } else if (!cancelled && !stopped) {
          handlers.onError({
            code: 'network',
            message: 'Transcription connection error',
          });
        }
      };

      ws.onclose = () => {
        if (!opened) {
          reject({
            code: 'network',
            message: 'Transcription socket closed',
          } as SttError);
        }
      };
    });
  };

  const stop = async (): Promise<void> => {
    // Stop sending mic audio, signal end-of-stream, give AWS a beat to flush
    // the final result, then close. `stopped` (not `cancelled`) so onmessage
    // keeps committing the trailing finals that arrive during the flush.
    stopped = true;
    teardownAudio();
    try {
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(encodeAudioEvent(new Uint8Array(0)));
        await new Promise((r) => setTimeout(r, FLUSH_MS));
      }
    } catch (err) {
      logger.error('transcribe recognizer: stop flush failed', err);
    } finally {
      closeSocket();
    }
  };

  const cancel = (): void => {
    cancelled = true;
    teardownAudio();
    closeSocket();
  };

  return {start, stop, cancel};
}

export function createStreamingRecognizer(
  opts: CreateStreamingRecognizerOptions,
): StreamingRecognizer {
  const languageCode = opts.lang ?? 'en-US';
  return createAmazonTranscribeRecognizer(
    opts.getPresignedUrl,
    languageCode,
    opts.deviceId ?? '',
  );
}
